import { Button } from '@shopify/polaris';

type MiniBannerTone = 'success' | 'warning' | 'critical' | 'info';

function toneColors(tone: MiniBannerTone) {
    switch (tone) {
        case 'success': return { bg: 'rgba(16,185,129,0.12)', border: '#10B981', fg: '#065F46' }; // green
        case 'warning': return { bg: 'rgba(245,158,11,0.12)', border: '#F59E0B', fg: '#7C2D12' }; // amber
        case 'critical': return { bg: 'rgba(239,68,68,0.12)', border: '#EF4444', fg: '#7F1D1D' }; // red
        default: return { bg: 'rgba(145,208,255,0.12)', border: '#75b0ff', fg: '#00459f' }; // blue
    }
}

export function MiniBanner({
    tone = 'info',
    title,
    children,
    onDismiss,
    actions,
}: {
    tone?: MiniBannerTone;
    title?: string;
    children?: React.ReactNode;
    onDismiss?: () => void;
    actions?: Array<{ content: string; onAction: () => void; ariaLabel?: string }>;
}) {
    const c = toneColors(tone);
    return (
        <div
            role={tone === 'critical' ? 'alert' : 'status'}
            aria-live={tone === 'critical' ? 'assertive' : 'polite'}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: 10,
                borderLeft: `3px solid ${c.border}`,
                background: c.bg,
                color: c.fg,
                borderRadius: 8,
            }}
        >
            {/* Icon (inline SVG to avoid imports) */}
            <div aria-hidden style={{ lineHeight: 0, marginTop: 1 }}>
                {tone === 'success' ? (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill={c.border}><path d="M7.629 13.314l-3.3-3.3 1.414-1.414 1.886 1.886 4.714-4.714 1.414 1.414-6.128 6.128z" /></svg>
                ) : tone === 'critical' ? (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill={c.border}><path d="M10 2l8 14H2L10 2zm0 4.5l-.75 5.5h1.5L10 6.5zm0 7.5a1 1 0 100 2 1 1 0 000-2z" /></svg>
                ) : tone === 'warning' ? (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill={c.border}><path d="M10 2l8 14H2L10 2zm-1 5h2v5H9V7zm0 6h2v2H9v-2z" /></svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill={c.border}><path d="M9 8h2v6H9V8zm1-4a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" /></svg>
                )}
            </div>

            <div style={{ flex: 1 }}>
                {title && <div style={{ fontWeight: 600, marginBottom: 2 }}>{title}</div>}
                {children && <div style={{ fontSize: 13, lineHeight: 1.4 }}>{children}</div>}
                {!!actions?.length && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {actions.map((a, i) => (
                            <Button variant="secondary"
                                key={i}
                                onClick={a.onAction}
                                accessibilityLabel={a.ariaLabel || a.content}
                            >
                                {a.content}
                            </Button>
                        ))}
                    </div>
                )}
            </div>

            {onDismiss && (
                <button
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    style={{
                        border: 'none',
                        background: 'transparent',
                        color: c.fg,
                        cursor: 'pointer',
                        padding: 2,
                        lineHeight: 0,
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill={c.fg}><path d="M5 5l10 10m0-10L5 15" stroke={c.fg} strokeWidth="2" strokeLinecap="round" /></svg>
                </button>
            )}
        </div>
    );
}
